import { cn } from '@/lib/utils';
import { AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

type InfoBannerVariant = 'info' | 'warning' | 'success';

const VARIANT_STYLES: Record<InfoBannerVariant, { wrap: string; icon: React.ComponentType<{ className?: string }> }> = {
  info: { wrap: 'border-blue-200 bg-blue-50 text-blue-900', icon: Info },
  warning: { wrap: 'border-amber-200 bg-amber-50 text-amber-900', icon: AlertTriangle },
  success: { wrap: 'border-emerald-200 bg-emerald-50 text-emerald-900', icon: CheckCircle2 },
};

interface IInfoBannerProps {
  title?: string;
  children?: React.ReactNode;
  variant?: InfoBannerVariant;
  /** Override the default per-variant icon. */
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * Inline status banner (info / warning / success). Shared across the bot config
 * surfaces (Book Demo, Video Avatar) so callouts read identically everywhere.
 */
const InfoBanner: React.FC<IInfoBannerProps> = ({
  title, children, variant = 'warning', icon,
}) => {
  const style = VARIANT_STYLES[variant];
  const Icon = icon ?? style.icon;
  return (
    <div className={cn('flex gap-3 rounded-lg border px-4 py-3 text-sm', style.wrap)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && (
          <p className={cn('font-normal', title && 'mt-0.5 opacity-90')}>{children}</p>
        )}
      </div>
    </div>
  );
};

export default InfoBanner;
export type { InfoBannerVariant };
